import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Input,
  InputNumber,
  Radio,
  Space,
  Switch,
  Typography,
  message,
} from "antd";
import { CopyOutlined, SaveOutlined } from "@ant-design/icons";
import {
  CAPTURE_MODES,
  CHANNEL_TYPES,
  DISPLAY_MODES,
  MODE_META,
} from "./constants";
import {
  buildCaptureEmbedSnippet,
  channelSlug,
  channelUrl,
} from "./feedbackService";
import VoiceWidgetPreview from "./VoiceWidgetPreview";
import styles from "./style.module.scss";

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const emptyForm = () => ({
  name: "",
  description: "",
  prompt: "",
  modes: [CAPTURE_MODES.VOICE, CAPTURE_MODES.TEXT],
  maxDurationSeconds: 120,
  linkQrEnabled: false,
  webEmbedEnabled: false,
  displayMode: "popup",
});

const VoiceWidgetBuilderDrawer = ({
  open,
  widget,
  saving,
  apiReady = true,
  voiceOnly = false,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const isEdit = !!widget;
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({ name: null, prompt: null });

  useEffect(() => {
    if (!open) return;
    if (widget) {
      const linkCh = (widget.channels || []).find(
        (c) => c.channel_type === CHANNEL_TYPES.LINK_QR,
      );
      const embedCh = (widget.channels || []).find(
        (c) => c.channel_type === CHANNEL_TYPES.WEB_EMBED,
      );
      setForm({
        name: widget.name || "",
        description: widget.description || "",
        prompt: widget.prompt || "",
        modes: widget.modes?.length ? widget.modes : [CAPTURE_MODES.VOICE, CAPTURE_MODES.TEXT],
        maxDurationSeconds: widget.maxDurationSeconds || 120,
        linkQrEnabled: !!linkCh?.enabled,
        webEmbedEnabled: !!embedCh?.enabled,
        displayMode: embedCh?.config?.display_mode || "popup",
      });
    } else {
      setForm(
        voiceOnly
          ? { ...emptyForm(), modes: [CAPTURE_MODES.VOICE] }
          : emptyForm()
      );
    }
    setErrors({ name: null, prompt: null });
  }, [open, widget, voiceOnly]);

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const toggleMode = (mode, checked) => {
    setForm((prev) => {
      const next = new Set(prev.modes || []);
      if (checked) next.add(mode);
      else next.delete(mode);
      return { ...prev, modes: [...next] };
    });
  };

  const validate = () => {
    const next = { name: null, prompt: null };
    if (!form.name.trim()) next.name = t("voiceCaptureManagement.err_title_required");
    if (!form.prompt.trim()) next.prompt = t("voiceCaptureManagement.err_prompt_required");
    if (!form.modes?.length) next.modes = t("voiceCaptureManagement.err_mode_required");
    setErrors(next);
    return !next.name && !next.prompt && form.modes?.length > 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      message.error(t("voiceCaptureManagement.err_fix_fields"));
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      prompt: form.prompt.trim(),
      modes: form.modes,
      maxDurationSeconds: form.maxDurationSeconds,
    };
    const channels = isEdit
      ? [
          {
            channel_type: CHANNEL_TYPES.LINK_QR,
            enabled: form.linkQrEnabled,
            config: {},
          },
          {
            channel_type: CHANNEL_TYPES.WEB_EMBED,
            enabled: form.webEmbedEnabled,
            config: { display_mode: form.displayMode },
          },
        ]
      : null;
    await onSave(payload, channels);
  };

  const embedSlug =
    widget && (channelSlug(widget, CHANNEL_TYPES.WEB_EMBED) || channelSlug(widget, CHANNEL_TYPES.LINK_QR));
  const publicUrl = widget && channelUrl(widget, CHANNEL_TYPES.LINK_QR);
  const embedSnippet =
    widget && embedSlug && form.webEmbedEnabled
      ? buildCaptureEmbedSnippet(embedSlug, {
          modes: form.modes,
          displayMode: form.displayMode,
          prompt: form.prompt,
        })
      : null;

  const copyText = async (text, okKey) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(t(okKey));
    } catch (_) {
      message.error(t("voiceCaptureManagement.copy_failed"));
    }
  };

  const title = useMemo(
    () =>
      isEdit
        ? t("voiceCaptureManagement.edit_widget")
        : t("voiceCaptureManagement.create_widget"),
    [isEdit, t],
  );

  return (
    <Drawer
      title={title}
      width={980}
      open={open}
      onClose={onClose}
      destroyOnClose
      footer={
        <Space>
          <Button type="text" onClick={onClose}>
            {t("voiceCaptureManagement.cancel")}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            {isEdit
              ? t("voiceCaptureManagement.save_changes")
              : t("voiceCaptureManagement.save_widget")}
          </Button>
        </Space>
      }
    >
      <div className={styles.drawerLayout}>
        <div className={styles.drawerFormCol}>
      <div className={styles.drawerSection}>
        <Text type="secondary">{t("voiceCaptureManagement.builder_intro")}</Text>
      </div>

      <div className={styles.drawerSection}>
        <label className={styles.fieldLabel}>{t("voiceCaptureManagement.field_title")}</label>
        <Input
          value={form.name}
          onChange={(e) => setField("name", e.target.value)}
          placeholder={t("voiceCaptureManagement.field_title_placeholder")}
          status={errors.name ? "error" : undefined}
        />
        {errors.name ? <span className={styles.fieldError}>{errors.name}</span> : null}
      </div>

      <div className={styles.drawerSection}>
        <label className={styles.fieldLabel}>{t("voiceCaptureManagement.field_description")}</label>
        <TextArea
          rows={2}
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
          placeholder={t("voiceCaptureManagement.field_description_placeholder")}
        />
      </div>

      <div className={styles.drawerSection}>
        <label className={styles.fieldLabel}>{t("voiceCaptureManagement.field_prompt")}</label>
        <TextArea
          rows={2}
          value={form.prompt}
          onChange={(e) => setField("prompt", e.target.value)}
          placeholder={t("voiceCaptureManagement.field_prompt_placeholder")}
          status={errors.prompt ? "error" : undefined}
        />
        <span className={styles.fieldHint}>{t("voiceCaptureManagement.field_prompt_hint")}</span>
        {errors.prompt ? <span className={styles.fieldError}>{errors.prompt}</span> : null}
      </div>

      <div className={styles.drawerSection}>
        <label className={styles.fieldLabel}>{t("voiceCaptureManagement.field_modes")}</label>
        <Space direction="vertical">
          {Object.values(CAPTURE_MODES).map((mode) => {
            const meta = MODE_META[mode];
            return (
              <Checkbox
                key={mode}
                checked={form.modes?.includes(mode)}
                disabled={meta?.disabled}
                onChange={(e) => toggleMode(mode, e.target.checked)}
              >
                {t(meta.labelKey)}
                {meta?.disabled ? ` (${t("voiceCaptureManagement.coming_soon")})` : ""}
              </Checkbox>
            );
          })}
        </Space>
      </div>

      <div className={styles.drawerSection}>
        <label className={styles.fieldLabel}>{t("voiceCaptureManagement.field_max_duration")}</label>
        <InputNumber
          min={30}
          max={300}
          value={form.maxDurationSeconds}
          onChange={(v) => setField("maxDurationSeconds", v || 120)}
          addonAfter={t("voiceCaptureManagement.seconds")}
          style={{ width: "100%" }}
        />
      </div>

      {isEdit ? (
        <>
          <div className={styles.drawerDivider} />
          <div className={styles.drawerSection}>
            <Text strong>{t("voiceCaptureManagement.distribution_title")}</Text>
            <Paragraph type="secondary" className={styles.distributionDesc}>
              {t("voiceCaptureManagement.distribution_desc")}
            </Paragraph>
            <div className={styles.channelRow}>
              <Switch
                checked={form.linkQrEnabled}
                onChange={(v) => setField("linkQrEnabled", v)}
              />
              <span>{t("voiceCaptureManagement.channel_link_qr")}</span>
            </div>
            {form.linkQrEnabled && publicUrl ? (
              <Alert
                type="info"
                showIcon
                className={styles.snippetBox}
                message={
                  <Space>
                    <Text code className={styles.snippetCode}>{publicUrl}</Text>
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() =>
                        copyText(publicUrl, "voiceCaptureManagement.copied_link")
                      }
                    />
                  </Space>
                }
              />
            ) : null}
            <div className={styles.channelRow}>
              <Switch
                checked={form.webEmbedEnabled}
                onChange={(v) => setField("webEmbedEnabled", v)}
              />
              <span>{t("voiceCaptureManagement.channel_web_embed")}</span>
            </div>
            {form.webEmbedEnabled ? (
              <Radio.Group
                value={form.displayMode}
                onChange={(e) => setField("displayMode", e.target.value)}
                className={styles.displayModes}
              >
                {DISPLAY_MODES.map((dm) => (
                  <Radio key={dm.value} value={dm.value}>
                    {t(dm.labelKey)}
                  </Radio>
                ))}
              </Radio.Group>
            ) : null}
            {embedSnippet ? (
              <div className={styles.snippetBox}>
                <div className={styles.snippetHeader}>
                  <Text strong>{t("voiceCaptureManagement.embed_snippet")}</Text>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() =>
                      copyText(embedSnippet, "voiceCaptureManagement.copied_embed")
                    }
                  >
                    {t("voiceCaptureManagement.copy")}
                  </Button>
                </div>
                <pre className={styles.snippetPre}>{embedSnippet}</pre>
              </div>
            ) : form.webEmbedEnabled ? (
              <Alert
                type="warning"
                showIcon
                message={t("voiceCaptureManagement.embed_after_save")}
              />
            ) : null}
          </div>
        </>
      ) : null}
        </div>
        <div className={styles.drawerPreviewCol}>
          <VoiceWidgetPreview
            prompt={form.prompt}
            modes={form.modes}
            displayMode={form.displayMode}
            maxDurationSeconds={form.maxDurationSeconds}
          />
        </div>
      </div>
    </Drawer>
  );
};

export default VoiceWidgetBuilderDrawer;
